import { CommonErrorCode, SiteSwitchErrorCodes } from '../helpers/ErrorCodes';
import { Module } from '../Module';
import { IError } from '../ServerApi/types/IError';
import { ServerService } from './ServerService';
import { StartPageService } from './StartPageService';
import { StateWrapperService } from './StateWrapperService';

export class SiteSwitchService {

  public static $inject = [
    '$location',
    '$q',
    '$rootScope',
    '$state',
    'ServerService',
    'StartPageService',
    'StateWrapperService',
  ];

  private previousServerUrl: string;
  private previousSiteUrl: string;

  constructor(private $location: ng.ILocationService,
              private $q: ng.IQService,
              $rootScope: ng.IScope,
              private $state: ng.ui.IStateService,
              private server: ServerService,
              private startPageService: StartPageService,
              private stateWrapper: StateWrapperService,
  ) {
      $rootScope.$on('$locationChangeSuccess', () => {
      const toState: ng.ui.IState = this.stateWrapper.getMatchingState(
        this.$location.path(), this.$location.search());

      if(this.stateWrapper.isDescendantOf(toState, 'main.server')) {
        this.previousServerUrl = this.$location.url();
      } else if (stateWrapper.isDescendantOf(toState, 'main.site')) {
        this.previousSiteUrl = this.$location.url();
      }
    });
  }

  public static getSiteUrlFragment(urlName: string): string {
    return `/#/site/${urlName}/`;
  }

  /**
  * Switches the site if the given urlName if it's not the current site's urlName
  *
  * Use this instead of ServerService.switchSiteIfNeeded in order to give proper
  * feedback in the error conditions
  */
  public switchIfNeeded(urlName: string, podRedirectPath?: string): ng.IPromise<void> {
    if (podRedirectPath === undefined) {
      podRedirectPath = SiteSwitchService.getSiteUrlFragment(urlName);
    }
    this.previousSiteUrl = null;

    return this.server.switchSiteIfNeeded_DoNotCallDirectly(urlName, podRedirectPath)
      .catch((error: IError) => {
        const errorLocKey = this.getSwitchSiteErrorLocKey(error);
        // This error code is returned when the current user doesn't have access to the site they're trying to switch to.
        // The user must log in using different credentials, specific to the site. For now, it is supported only when
        // third party auth is configured (i.e. SiteSAML is enabled).
        if (error && error.code === CommonErrorCode.SiteUserNotFound && this.server.isSiteSamlEnabled()) {
          this.$state.go('login.authenticate', {
            site: urlName
          });
        } else if (this.server.isSessionMultiSite()) {
          // If multi-site, let the user choose which site to go to
          this.$state.go('login.error.noSite', {
            detailsLocKey: errorLocKey,
            redirectState: 'login.site',
            redirectLocKey: 'goToSiteList'
          });
        } else {
          // We're single site, or this is our first state  (from a full page reload)
          // TODO: Should have a redirect on error page to precise last state or user's start page
          this.$state.go('login.error.noSite', {
            detailsLocKey: errorLocKey
          });
        }
        return this.$q.reject(error);
      });
  }

  public switchIfNeededAndRedirectToStartPage(urlName: string, podRedirectPath?: string): ng.IPromise<void> {
    return this.switchIfNeeded(urlName, podRedirectPath).then(() => {
      this.$location.url(this.startPageService.getCurrentUserStartPageForCurrentSite());
    });
  }

  private getSwitchSiteErrorLocKey(error: IError) {
    switch (error.code) {
      case SiteSwitchErrorCodes.UserNotMemberOfSiteOrSiteNotExist:
        return 'switchSiteFailedNotMemberOrNotExist';
      case SiteSwitchErrorCodes.SiteLocked:
        return 'switchSiteFailedSiteLocked';
      case SiteSwitchErrorCodes.SiteSuspended:
        return 'switchSiteFailedSiteSuspended';
      case SiteSwitchErrorCodes.UserUnlicensedForSite:
        return 'switchSiteFailedUserUnlicensed';
      default:
        return 'switchSiteFailedUnknown';
    }
  }

  public switchIfNeededNoFeedback(urlName: string): ng.IPromise<void> {
    this.previousSiteUrl = null;
    return this.server.switchSiteIfNeeded_DoNotCallDirectly(urlName);
  }
}

Module.instance().service('SiteSwitchService', SiteSwitchService);
